import React, { useState } from 'react';
import { X, Check, Mail, User, Building, Phone, ArrowRight, Globe } from 'lucide-react';

const WHATSAPP_URL = `https://wa.me/447757233260?text=${encodeURIComponent(
    `Hi! I just signed up on your website. Looking forward to learning more about Qorvix.`
)}`;

export default function SignupFormModal({ isOpen, onClose, buttonType = 'demo' }) {
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        companyWebsite: '',
        businessType: '',
        monthlyLeads: '',
        currentChallenge: '',
        preferredTime: ''
    });

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleNext = () => {
        if (step < 3) {
            setStep(step + 1);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/signup`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    buttonType,
                    timestamp: new Date().toISOString()
                })
            });

            if (response.ok) {
                setIsSuccess(true);
                // Force redirect to WhatsApp — navigation (not popup) is never blocked
                setTimeout(() => {
                    window.location.href = WHATSAPP_URL;
                }, 1500);
            } else {
                alert('Something went wrong. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };
    const resetForm = () => {
        setStep(1);
        setIsSuccess(false);
        setFormData({
            fullName: '',
            email: '',
            phone: '',
            company: '',
            companyWebsite: '',
            businessType: '',
            monthlyLeads: '',
            currentChallenge: '',
            preferredTime: ''
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal */}
            <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors z-10"
                >
                    <X size={24} />
                </button>

                {isSuccess ? (
                    // Success State
                    <div className="p-12 text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="text-green-600" size={40} strokeWidth={3} />
                        </div>
                        <h2 className="text-3xl font-bold mb-4">Thank You!</h2>
                        <p className="text-lg text-gray-600 mb-2">
                            We've received your information and sent a confirmation to your email.
                        </p>
                        <p className="text-gray-600">
                            Our team will get back to you within 24 hours.
                        </p>
                    </div>
                ) : (
                    <div className="p-10">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold mb-2">
                                {buttonType === 'demo' ? 'Start Your Free Demo' : 'Get Started Now'}
                            </h2>
                            <p className="text-gray-600">
                                Tell us about your business and we'll be in touch shortly
                            </p>
                        </div>

                        {/* Progress Indicator */}
                        <div className="flex justify-center gap-2 mb-8">
                            {[1, 2, 3].map((s) => (
                                <div
                                    key={s}
                                    className={`h-2 w-20 rounded-full transition-all ${s <= step ? 'bg-orange-500' : 'bg-gray-200'
                                        }`}
                                ></div>
                            ))}
                        </div>

                        {/* Step 1: Basic Info */}
                        {step === 1 && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold mb-6">Your Information</h3>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Full Name *</label>
                                    <div className="relative">
                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="text"
                                            value={formData.fullName}
                                            onChange={(e) => handleChange('fullName', e.target.value)}
                                            placeholder="John Smith"
                                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Email Address *</label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleChange('email', e.target.value)}
                                            placeholder="john@company.com"
                                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Phone Number *</label>
                                    <div className="relative">
                                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => handleChange('phone', e.target.value)}
                                            placeholder="+44 7700 900000"
                                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Company Name *</label>
                                    <div className="relative">
                                        <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="text"
                                            value={formData.company}
                                            onChange={(e) => handleChange('company', e.target.value)}
                                            placeholder="Your Company Ltd"
                                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Company Website *</label>
                                    <div className="relative">
                                        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="url"
                                            value={formData.companyWebsite}
                                            onChange={(e) => handleChange('companyWebsite', e.target.value)}
                                            placeholder="https://yourcompany.com"
                                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                                            required
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={handleNext}
                                    disabled={!formData.fullName || !formData.email || !formData.phone || !formData.company || !formData.companyWebsite}
                                    className="w-full bg-orange-500 text-white py-4 rounded-lg font-semibold hover:bg-orange-600 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    Next Step <ArrowRight size={20} />
                                </button>
                            </div>
                        )}

                        {/* Step 2: Business Details */}
                        {step === 2 && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold mb-6">About Your Business</h3>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Business Type *</label>
                                    <select
                                        value={formData.businessType}
                                        onChange={(e) => handleChange('businessType', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors appearance-none bg-white"
                                        required
                                    >
                                        <option value="">Select your business type</option>
                                        <option value="service">Service-based</option>
                                        <option value="ecommerce">E-commerce</option>
                                        <option value="agency">Agency</option>
                                        <option value="saas">SaaS</option>
                                        <option value="local">Local business</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">How many leads do you get monthly? *</label>
                                    <select
                                        value={formData.monthlyLeads}
                                        onChange={(e) => handleChange('monthlyLeads', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors appearance-none bg-white"
                                        required
                                    >
                                        <option value="">Select range</option>
                                        <option value="0-50">0-50 leads</option>
                                        <option value="50-200">50-200 leads</option>
                                        <option value="200-500">200-500 leads</option>
                                        <option value="500+">500+ leads</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">What's your biggest challenge? *</label>
                                    <select
                                        value={formData.currentChallenge}
                                        onChange={(e) => handleChange('currentChallenge', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors appearance-none bg-white"
                                        required
                                    >
                                        <option value="">Select your main challenge</option>
                                        <option value="lead-response">Slow lead response time</option>
                                        <option value="follow-up">Inconsistent follow-up</option>
                                        <option value="qualification">Lead qualification</option>
                                        <option value="scaling">Scaling operations</option>
                                        <option value="automation">Lack of automation</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setStep(1)}
                                        className="flex-1 border-2 border-gray-300 text-gray-700 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleNext}
                                        disabled={!formData.businessType || !formData.monthlyLeads || !formData.currentChallenge}
                                        className="flex-1 bg-orange-500 text-white py-4 rounded-lg font-semibold hover:bg-orange-600 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        Next Step <ArrowRight size={20} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Scheduling */}
                        {step === 3 && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold mb-6">When Should We Contact You?</h3>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Preferred Contact Time *</label>
                                    <select
                                        value={formData.preferredTime}
                                        onChange={(e) => handleChange('preferredTime', e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors appearance-none bg-white"
                                        required
                                    >
                                        <option value="">Select your preferred time</option>
                                        <option value="morning">Morning (9am - 12pm)</option>
                                        <option value="afternoon">Afternoon (12pm - 5pm)</option>
                                        <option value="evening">Evening (5pm - 7pm)</option>
                                        <option value="anytime">Anytime</option>
                                    </select>
                                </div>

                                <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6">
                                    <h4 className="font-bold mb-2">What Happens Next?</h4>
                                    <ul className="space-y-2 text-sm text-gray-700">
                                        <li className="flex items-start gap-2">
                                            <Check className="text-orange-500 flex-shrink-0 mt-0.5" size={16} />
                                            <span>You'll receive a confirmation email immediately</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check className="text-orange-500 flex-shrink-0 mt-0.5" size={16} />
                                            <span>Our team will review your information</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check className="text-orange-500 flex-shrink-0 mt-0.5" size={16} />
                                            <span>We'll contact you within 24 hours to schedule your demo</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setStep(2)}
                                        className="flex-1 border-2 border-gray-300 text-gray-700 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!formData.preferredTime || isSubmitting}
                                        className="flex-1 bg-orange-500 text-white py-4 rounded-lg font-semibold hover:bg-orange-600 transition-all disabled:bg-gray-300 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? 'Submitting...' : 'Complete Signup'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}